import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import "../common/commonStyles.css";

const NewProductsTransactionReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState([]);
  const [reportSummary, setReportSummary] = useState({});
  
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const [dates, setDates] = useState([startOfMonth, endOfMonth]);
  const [reportrange, setReportrange] = useState('');
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
    const startDate = format(value[0], 'dd-MM-yyyy');
    const endDate = format(value[1], 'dd-MM-yyyy');
    setReportrange(startDate + ' - ' + endDate);
    setPage(0);
  };

  const handleProductChange = (event, newValue) => {
    setSelectedProduct(newValue);
    setPage(0);
  };

  const filterProductOptions = (options, { inputValue }) =>
    options.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
      (option.code && option.code.toLowerCase().includes(inputValue.toLowerCase()))
    );

  const fetchProductOptions = useCallback(async () => {
    if (!accessToken || !dates[0] || !dates[1]) return;
    
    let startDate = format(dates[0], 'dd/MM/yyyy');
    let endDate = format(dates[1], 'dd/MM/yyyy');
    let pdtrange = startDate + ' - ' + endDate;
    
    const formData = new FormData();
    formData.append('reportrange', pdtrange);

    try {
      const response = await fetch(`${config.API_URL}/purchases/getProducts`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      
      const data = await response.json();
      const formattedOptions = (data.products || []).map(item => ({
        label: `${item.name} (${item.code})`,
        value: item.id,
        code: item.code,
        name: item.name
      }));

      setProductOptions(formattedOptions);
    } catch (error) {
      setError('Failed to load product options');
    }
  }, [accessToken, dates]);

  const fetchReportData = useCallback(async () => {
    if (!accessToken || !dates[0] || !dates[1]) return;
    
    setLoading(true);
    setError('');
    
    try {
      let startDate = format(dates[0], 'dd/MM/yyyy');
      let endDate = format(dates[1], 'dd/MM/yyyy');
      let pdtrange = startDate + ' - ' + endDate;
      
      const formData = new FormData();
      formData.append('draw', String(page + 1));
      formData.append('start', String(page * rowsPerPage));
      formData.append('length', String(rowsPerPage));
      formData.append('reportrange', pdtrange);
      formData.append('type', 'product_wise');
      formData.append('product_id', selectedProduct ? selectedProduct.value : '');
      formData.append('pick_list', '');
      formData.append('sales_rep_id', '');

      const response = await fetch(`${config.API_URL}/productsTransactionReport`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report data');
      }

      const data = await response.json();
      
      const transformedData = (data.data || []).map((item, index) => ({
        id: item.id || (index + 1),
        order_num: item.reference_no || '',
        picklist_no: item.picklist_number || '',
        order_no: item.date || '',
        manifest_number: item.manifest_id || '',
        status: item.sale_status || '',
        delivery_date: item.delivery_date || '',
        order_qty: parseFloat(item.order_qty || 0),
        accept_qty: parseFloat(item.accept_qty || 0),
        picked_qty: parseFloat(item.picked_qty || 0),
        confirmed_qty: parseFloat(item.confirmed_qty || 0),
        delivery_qty: parseFloat(item.delivery_qty || 0)
      }));

      setReportData(transformedData);
      setTotalCount(data.recordsTotal || data.recordsFiltered || transformedData.length);
      setReportSummary({
        order_total: data.totals?.totalorder_qty || 0,
        missing_total: data.totals?.totalaccept_qty || 0,
        order_qty: data.totals?.totalorder_qty || 0,
        accept_qty: data.totals?.totalaccept_qty || 0,
        perform_qty: data.totals?.toalperform_qty || 0,
        picked_qty: data.totals?.totalpicked_qty || 0,
        confirmed_qty: data.totals?.totalconfirmed_qty || 0,
        delivery_qty: data.totals?.totaldelivery_qty || 0
      });
    } catch (error) {
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [accessToken, dates, page, rowsPerPage, selectedProduct]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchProductOptions();
  }, [accessToken, dates, fetchProductOptions]);

  useEffect(() => {
    if (dates[0] && dates[1]) {
      fetchReportData();
    }
  }, [fetchReportData]);

  const exportToExcel = () => {
    if (reportData.length === 0) {
      return;
    }

    const exportData = reportData.map((item, index) => ({
      "SR. No": page * rowsPerPage + index + 1,
      "Order Date": item.order_no,
      "Order Number": item.order_num,
      "Picklist Number": item.picklist_no,
      "Manifest Number": item.manifest_number,
      "Delivery Date": item.delivery_date,
      "Order Qty": item.order_qty,
      "Accept Qty": item.accept_qty,
      "Picked Qty": item.picked_qty,
      "Confirmed Qty": item.confirmed_qty,
      "Delivery Qty": item.delivery_qty,
      "Status": item.status
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ProductsTransactionReport');

    const now = new Date();
    const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const formattedTime = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
    const filename = `products-transaction-report-${formattedDate}-${formattedTime}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    if (status === 'Delivered') return 'success';
    if (status === 'Intransit') return 'warning';
    if (status === 'Reject') return 'error';
    return 'info';
  };

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
          <Paper className="paper-style">
            <Box sx={{ px: 2, py: 1 }}>
              <Grid2 container spacing={1} display="flex" justifyContent="space-between">
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography className="page-title">
                    Products Transaction Report
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md:4}}>
                  <Autocomplete
                    className="input-border"
                    size="small"
                    options={productOptions}
                    value={selectedProduct}
                    onChange={handleProductChange}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    filterOptions={filterProductOptions}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li key={key} {...optionProps}>
                          <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {option.code}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Product"
                      label="Product"
                      className="text-field input-border"
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md:3}} display="flex" justifyContent="flex-end">
                  <Box sx={{ width: {xs: '100%', md: '330px'} }}> 
                  <CommonDatePicker
                    className="input-border"
                    value={dates}
                    showOneCalendar
                    onChange={handleDateChange}
                    format="dd/MM/yyyy"
                    character=" - "
                  />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      className="button-variant-contained"
                      variant="contained"
                  
                      fullWidth
                      onClick={fetchReportData}
                      disabled={loading}
                    
                    >
                      {loading ? 'Loading...' : 'Submit'}
                    </Button>
                    <Button
                      className="button-variant-outlined"
                      variant="outlined"
                  
                      fullWidth
                      onClick={exportToExcel}
                      disabled={reportData.length === 0}
                      startIcon={<Iconify icon="eva:download-outline" width={18} />}
                    >
                      Export
                    </Button>
                  </Stack>
                </Grid2>
              </Grid2>
            </Box>

            <TableContainer
            >
              <Table size="small" className="table-cell-border">
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell sx={{ width: 80 }}>SR. No</TableCell>
                    <TableCell width="100px" >Order Date</TableCell>
                    <TableCell width="90px" >Order No</TableCell>
                    <TableCell width="100px" >Picklist No</TableCell>
                    <TableCell width="100px" >Manifest No</TableCell>
                    <TableCell width="100px" >Delivery Date</TableCell>
                    <TableCell width="100px" align="right">Order Qty</TableCell>
                    <TableCell width="100px" align="right">Accept Qty</TableCell>
                    <TableCell width="100px" align="right">Picked Qty</TableCell>
                    <TableCell width="100px" align="right">Confirmed Qty</TableCell>
                    <TableCell width="100px" align="right">Delivery Qty</TableCell>
                    <TableCell width="80px" >Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : reportData.length > 0 ? (
                    reportData.map((row, index) => (
                      <TableRow key={row.id || index} hover className="table-cell-style">
                        <TableCell sx={{ textAlign: 'center' }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>{row.order_no || ''}</TableCell>
                        <TableCell>{row.order_num || ''}</TableCell>
                        <TableCell>{row.picklist_no || ''}</TableCell>
                        <TableCell>{row.manifest_number || ''}</TableCell>
                        <TableCell>{row.delivery_date || ''}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: row.order_qty > 0 ? '#f14240' : '#212b36', fontWeight: row.order_qty > 0 ? 600 : 400 }}>
                          {row.order_qty || 0}
                        </TableCell>
                        <TableCell align="right">{row.accept_qty || 0}</TableCell>
                        <TableCell align="right">{row.picked_qty || 0}</TableCell>
                        <TableCell align="right">{row.confirmed_qty || 0}</TableCell>
                        <TableCell align="right">{row.delivery_qty || 0}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.status || ''}
                            color={getStatusColor(row.status)}
                            variant="soft"
                            size="small"
                            sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4, color: '#0e0f0fef', fontSize: '0.75rem' }}>
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow className="table-cell-style"
                  sx={{
                    backgroundColor: '#f5f7fa',
                    '& .MuiTableCell-root': {
                      fontWeight: 600                
                    }
                  }}
                  >
                    <TableCell colSpan={6} align="right">
                      Totals:
                    </TableCell>
                    <TableCell align="right">
                      {reportSummary.order_qty || 0}
                    </TableCell>
                    <TableCell align="right">
                      {reportSummary.accept_qty || 0}
                    </TableCell>
                    <TableCell align="right">
                      {reportSummary.picked_qty || 0}
                    </TableCell>
                    <TableCell align="right">
                      {reportSummary.confirmed_qty || 0}
                    </TableCell>
                    <TableCell align="right">
                      {reportSummary.delivery_qty || 0}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              className="pagination-styles"
            />
          </Paper>
        </Box>

    </>
  );
};

export default NewProductsTransactionReport;
